// Local, per-user "save to collection" store.
//
// Persists in localStorage so saving works with zero backend, and can be
// promoted to an API later without touching the call sites. Emits a window
// event ("gwbi:collections-changed") so every SaveButton and the Collections
// page stay in sync across the app.

export const COLLECTIONS_EVENT = "gwbi:collections-changed";

const getUserId = () => {
  try {
    const direct =
      localStorage.getItem("userId") || sessionStorage.getItem("userId");
    if (direct) return String(direct);
    const raw =
      localStorage.getItem("userprofile") ||
      sessionStorage.getItem("userprofile");
    if (raw) {
      const u = JSON.parse(raw);
      return String(u?.id || u?.user_id || u?.userId || u?.user?.id || "anon");
    }
  } catch {
    /* ignore */
  }
  return "anon";
};

const storageKey = () => `gwbi.collections.${getUserId()}`;

const genId = () =>
  `c_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

const seed = () => [
  { id: "default", name: "Saved", createdAt: new Date().toISOString(), items: [] },
];

export const itemKey = (item) =>
  item?.key ||
  `${item?.type || "item"}:${item?.id ?? item?.link ?? item?.title ?? ""}`;

export function listCollections() {
  try {
    const raw = localStorage.getItem(storageKey());
    if (!raw) return seed();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length ? parsed : seed();
  } catch {
    return seed();
  }
}

function persist(list) {
  try {
    localStorage.setItem(storageKey(), JSON.stringify(list));
  } catch {
    /* ignore quota / serialization errors */
  }
  try {
    window.dispatchEvent(new Event(COLLECTIONS_EVENT));
  } catch {
    /* ignore */
  }
  return list;
}

export function createCollection(name) {
  const list = listCollections();
  const collection = {
    id: genId(),
    name: (name || "Untitled").trim() || "Untitled",
    createdAt: new Date().toISOString(),
    items: [],
  };
  persist([...list, collection]);
  return { collection };
}

export function renameCollection(id, name) {
  return persist(
    listCollections().map((c) =>
      c.id === id ? { ...c, name: (name || c.name).trim() || c.name } : c,
    ),
  );
}

export function deleteCollection(id) {
  return persist(listCollections().filter((c) => c.id !== id));
}

export function addItem(collectionId, item) {
  const key = itemKey(item);
  return persist(
    listCollections().map((c) => {
      if (c.id !== collectionId) return c;
      if (c.items.some((it) => it.key === key)) return c;
      return {
        ...c,
        items: [{ ...item, key, savedAt: new Date().toISOString() }, ...c.items],
      };
    }),
  );
}

export function removeItem(collectionId, key) {
  return persist(
    listCollections().map((c) =>
      c.id === collectionId
        ? { ...c, items: c.items.filter((it) => it.key !== key) }
        : c,
    ),
  );
}

export function isSaved(key) {
  return listCollections().some((c) => c.items.some((it) => it.key === key));
}

export function collectionIdsForItem(key) {
  return listCollections()
    .filter((c) => c.items.some((it) => it.key === key))
    .map((c) => c.id);
}

export function savedCount() {
  const keys = new Set();
  listCollections().forEach((c) => c.items.forEach((it) => keys.add(it.key)));
  return keys.size;
}
